"use client";

import * as React from "react";
import { PARITY } from "../../data/parity";
import { Reveal } from "../layout/Section";

/**
 * The constraint set, then the answer.
 *
 * Laid out as three ruled-out options followed by the approach, because the
 * reasoning is the part worth reading and a bullet list would flatten it.
 *
 * Everything under the lede is behind a disclosure. This is the longest block
 * on the site by a wide margin, and leaving it open pushed the rest of the
 * case study, the live demo included, off the end of a reasonable scroll. The
 * heading and lede stay out so the reader knows what they are declining.
 */
export default function ParityProblem() {
  const [open, setOpen] = React.useState(false);
  const [showGuards, setShowGuards] = React.useState(false);

  return (
    <div className="mt-12">
      <Reveal>
        <div className="border-t pt-4">
          <p className="eyebrow">{PARITY.eyebrow}</p>
          <h4 className="mt-3 max-w-3xl text-balance text-xl font-semibold leading-snug tracking-tight md:text-2xl">
            {PARITY.heading}
          </h4>
          <p className="prose-body mt-3 max-w-2xl text-[0.9375rem]">
            {PARITY.lede}
          </p>
        </div>
      </Reveal>

      <Reveal delay={60}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="mt-6 flex w-full items-start gap-4 rounded-xl border bg-raised p-5 text-left transition-colors hover:border-[rgb(var(--accent)/0.45)] md:p-6"
        >
          <span className="min-w-0 flex-1">
            <span className="eyebrow text-accent">{PARITY.moreLabel}</span>
            <span className="prose-body mt-2 block max-w-2xl text-[0.875rem]">
              {PARITY.moreHint}
            </span>
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            aria-hidden
            className="mt-0.5 shrink-0 text-faint transition-transform duration-300"
            style={{ transform: open ? "rotate(180deg)" : undefined }}
          >
            <path
              d="M4 6.5 8 10.5 12 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </Reveal>

      {open && (
        <>
          {/* The cascade. Each card is forced onto the table by the one before it,
          so the connective line matters as much as the card. */}
          <div className="mt-7 grid gap-px overflow-hidden rounded-xl border bg-[rgb(var(--line)/var(--line-a))] md:grid-cols-3">
            {PARITY.ruledOut.map((opt, i) => (
              <Reveal key={opt.name} delay={i * 70}>
                <div className="flex h-full flex-col bg-bg p-5">
                  <p className="font-mono text-2xs text-accent">
                    {String(i + 1).padStart(2, "0")} · {opt.because}
                  </p>

                  <div className="mt-2.5 flex items-start justify-between gap-3">
                    <h5 className="text-[0.9375rem] font-medium leading-snug tracking-tight text-ink">
                      {opt.name}
                    </h5>
                    <span
                      className="mt-0.5 shrink-0 rounded border border-rose-500/30 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-rose-600 dark:text-rose-400"
                      aria-label="Ruled out"
                    >
                      ruled out
                    </span>
                  </div>

                  <p className="prose-body mt-3 text-[0.8125rem]">{opt.how}</p>

                  <p className="eyebrow mt-4">Why not</p>
                  <p className="prose-body mt-1.5 text-[0.8125rem]">
                    {opt.blocker}
                  </p>

                  <p className="mt-auto pt-4 text-[0.8125rem] italic text-muted">
                    {opt.verdict}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Why this engine, given that its weakness was already priced in. */}
          <Reveal delay={60}>
            <div className="mt-6 grid gap-x-8 gap-y-2 md:grid-cols-[13rem_minmax(0,1fr)]">
              <p className="eyebrow md:pt-1">{PARITY.engine.eyebrow}</p>
              <p className="prose-body max-w-3xl text-[0.875rem]">
                {PARITY.engine.body}
              </p>
            </div>
          </Reveal>

          {/* The turn. */}
          <Reveal delay={80}>
            <div className="mt-8 rounded-xl border border-l-2 border-l-accent bg-raised p-5 md:p-7">
              <p className="eyebrow">{PARITY.pivot.eyebrow}</p>
              <p className="prose-body mt-3 max-w-3xl text-[0.9375rem]">
                {PARITY.pivot.body}
              </p>
            </div>
          </Reveal>

          {/* The run-up. Deliberately prose: this is the human beat, not a
          metrics panel. */}
          <Reveal delay={60}>
            <div className="mt-8 max-w-3xl space-y-3">
              {PARITY.cost.line.map((para, i) => (
                <p key={i} className="prose-body text-[0.9375rem]">
                  {para}
                </p>
              ))}
              <p className="pt-1 text-[0.9375rem] font-medium text-ink">
                {PARITY.cost.handoff}
              </p>
            </div>
          </Reveal>

          {/* the answer */}
          <Reveal delay={80}>
            <div className="mt-4 rounded-xl border bg-surface p-6 md:p-8">
              <p className="eyebrow text-accent">{PARITY.answerEyebrow}</p>
              <h5 className="mt-3 max-w-2xl text-balance text-lg font-semibold leading-snug tracking-tight md:text-xl">
                {PARITY.answerHeading}
              </h5>
              <div className="mt-4 max-w-3xl space-y-3">
                {PARITY.answer.map((para, i) => (
                  <p key={i} className="prose-body text-[0.9375rem]">
                    {para}
                  </p>
                ))}
              </div>

              {/* the seven guards */}
              <div className="mt-7 border-t pt-5">
                <button
                  type="button"
                  onClick={() => setShowGuards((v) => !v)}
                  className="flex w-full items-center gap-3 text-left"
                  aria-expanded={showGuards}
                >
                  <span className="eyebrow">
                    {PARITY.guardsEyebrow}
                    {"  "}
                    <span className="text-muted">
                      ({PARITY.guards.length} sources)
                    </span>
                  </span>
                  <span className="h-px flex-1 bg-[rgb(var(--line)/var(--line-a))]" />
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    aria-hidden
                    className="shrink-0 text-faint transition-transform duration-300"
                    style={{
                      transform: showGuards ? "rotate(180deg)" : undefined,
                    }}
                  >
                    <path
                      d="M4 6.5 8 10.5 12 6.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {showGuards && (
                  <ol className="mt-4 grid gap-x-8 gap-y-3 md:grid-cols-2">
                    {PARITY.guards.map((g, i) => (
                      <li
                        key={g.name}
                        className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2"
                      >
                        <span className="font-mono text-2xs text-faint">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span>
                          <span className="text-[0.8125rem] font-medium text-ink">
                            {g.name}
                          </span>
                          <span className="prose-body block text-[0.8125rem]">
                            {g.fix}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div className="mt-6 rounded-lg border-l-2 border-l-accent bg-raised px-4 py-3">
                <p className="text-[0.8125rem] font-medium text-ink">
                  {PARITY.standard.label}
                </p>
                <p className="prose-body mt-1 text-[0.8125rem]">
                  {PARITY.standard.body}
                </p>
              </div>
            </div>
          </Reveal>
        </>
      )}
    </div>
  );
}
